import dayjs from 'dayjs';
import omit from 'lodash/omit';
import pick from 'lodash/pick';

import { client } from 'app/services/GuestCenterService';
import { Pass } from 'app/services/GuestCenterService.types';
import {
  ReservationCancelResponse,
  ReservationCompleteRequestProps,
  ReservationCompleteResponse,
  ReservationDaysResponse,
  ReservationHoldRequestProps,
  ReservationHoldResponse,
  ReservationReleaseRequestProps,
  ReservationRequestProps,
  ReservationTimesResponse,
} from 'app/services/ReservationService.types';
import { DATE_FORMAT_ISO, sleep } from 'app/utils/time';

export const getDays = async ({
  email,
  attractionKey,
  locator,
  optionKey = null,
  start,
  end,
  passes,
  familyCode,
  isModify,
  addOns,
}: ReservationRequestProps) => {
  const startISO = dayjs(start).startOf('day').format(DATE_FORMAT_ISO);
  const endISO = dayjs(end).startOf('day').format(DATE_FORMAT_ISO);

  const { data } = await client.post<ReservationDaysResponse>(
    `/reserve/days/${locator}/attraction/${attractionKey}/${buildModifyParam(isModify)}`,
    {
      email,
      start: startISO,
      end: endISO,
      familyCode,
      optionKey,
      passes: passes.map((pass) => omit(pass, ['tickets'])),
      ...(addOns && addOns.length > 0 ? { addOns } : {}),
    }
  );

  return data;
};

export const getTimes = async ({
  email,
  attractionKey,
  locator,
  optionKey = null,
  start,
  end,
  passes,
  familyCode,
  isModify,
  addOns,
}: ReservationRequestProps) => {
  const startISO = dayjs(start).startOf('day').format(DATE_FORMAT_ISO);
  const endISO = dayjs(end).endOf('day').format(DATE_FORMAT_ISO);

  const { data } = await client.post<ReservationTimesResponse>(
    `/reserve/times/${locator}/attraction/${attractionKey}/?isModify=${isModify}`,
    {
      email,
      start: startISO,
      end: endISO,
      familyCode,
      optionKey,
      passes: passes.map((pass) => omit(pass, ['tickets'])),
      ...(addOns && addOns.length > 0 ? { addOns } : {}),
    }
  );

  const normalizedTimes = data?.times?.map((time) => {
    const payload = time as typeof time & {
      Type?: string;
      PriceData?: typeof time.priceData;
      PriceDataHash?: string;
      PriceDataHashKid?: string;
    };
    return {
      ...time,
      type: time.type ?? payload.Type,
      priceData: time.priceData ?? payload.PriceData,
      priceDataHash: time.priceDataHash ?? payload.PriceDataHash,
      priceDataHashKid: time.priceDataHashKid ?? payload.PriceDataHashKid,
    };
  });

  return normalizedTimes ? { ...data, times: normalizedTimes } : data;
};

export const holdTime = async ({
  email,
  attractionKey,
  locator,
  optionKey = null,
  passes,
  time: { timeSlotID, time },
  familyCode,
  isModify,
  addOns,
}: ReservationHoldRequestProps) => {
  const url = `/reserve/hold/${locator}/attraction/${attractionKey}/time/${encodeURIComponent(
    timeSlotID ?? 'unknown'
  )}/${buildModifyParam(isModify)}`;
  const body = {
    email,
    timeSlotID,
    heldTime: time,
    familyCode,
    optionKey,
    passes: passes.map((pass) => omit(pass, ['tickets'])),
    ...(addOns && addOns.length > 0 ? { addOns } : {}),
  };

  const { data } = await client.post<ReservationHoldResponse>(url, body);

  if (!data.success) throw data.error ?? new Error('An unknown error occurred');

  return data;
};

export const releaseTime = async ({
  email,
  attractionKey,
  locator,
  passes,
  hold: { holdReference },
  isModify,
}: ReservationReleaseRequestProps) => {
  const url = `/reserve/release/${locator}/attraction/${attractionKey}/order/${holdReference}/${buildModifyParam(
    isModify
  )}`;
  const body = {
    email,
    passes: passes.map((pass) =>
      pick(pass, ['barcode', 'ticketClass', 'validFrom', 'validTo', 'locator'])
    ),
  };

  const { data } = await client.post<ReservationHoldResponse>(url, body);
  return data;
};

export const completeTime = async ({
  email,
  attractionKey,
  locator,
  passes,
  time: { timeSlotID, time },
  hold,
  mock,
  option,
  dataItems,
  sendEmail,
  isModify,
  familyCode,
  addOns,
}: ReservationCompleteRequestProps) => {
  const url = `/reserve/complete/${locator}/attraction/${attractionKey}/order/${encodeURIComponent(
    hold?.holdReference ?? 'unknown'
  )}/${buildModifyParam(isModify)}`;
  const body = {
    heldTime: time,
    timeSlotID,
    email,
    sendEmail,
    dataItems,
    optionKey: option?.optionKey ?? null,
    familyCode,
    passes: passes.map((pass) =>
      pick(pass, ['barcode', 'ticketClass', 'validFrom', 'validTo', 'locator'])
    ),
    ...(addOns && addOns.length > 0 ? { addOns } : {}),
  };

  if (mock === 'success') return sleep(3000).then(() => ({ success: true, mock: true }));
  if (mock === 'error')
    return sleep(3000).then(() => Promise.reject(new Error('An epic failure occurred')));

  const { data } = await client.post<ReservationCompleteResponse>(url, body, {
    headers: {
      Accept: 'application/json',
    },
  });
  return data;
};

export const cancel = async ({
  email,
  attractionKey,
  locator,
  attractionOrderNumber,
  passes,
}: {
  email: string;
  attractionKey: string;
  locator: string;
  attractionOrderNumber: string;
  passes: Pass[];
}) => {
  const url = `/reserve/cancel/${locator}/attraction/${attractionKey}/order/${encodeURIComponent(
    attractionOrderNumber ?? 'unknown'
  )}/`;
  const body = {
    attractionOrderNumber,
    email,
    passes: passes.map((pass) => omit(pass, 'tickets')),
  };
  const { data } = await client.post<ReservationCancelResponse>(url, body);
  return data;
};

function buildModifyParam(value?: boolean) {
  return !value ? '' : `?isModify=${value}`;
}
